<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import { useRouter, useRoute } from 'vue-router';
  import TopbarOrganism from '@/components/organisms/topbar-organism/TopbarOrganism.vue';
  import InputMolecule from '@/components/molecules/input-molecule/InputMolecule.vue';
  import ButtonAtom from '@/components/atoms/ButtonAtom.vue';
  import coachService, {
    CreateCoachData,
  } from '@/services/coach/coach.service';
  import Coach from '@/models/coach/coach.model';
  import {
    SelectOption,
    transformToSelectOptions,
  } from '@/shared/selectOption';

  const router = useRouter();
  const route = useRoute();

  // Get coach ID from route params
  const coachId = route.params.id as string;

  // Form data
  const formData = ref<CreateCoachData>({
    coach_name: '',
    coach_lastname: '',
    coach_email: '',
    coach_headline: '',
    coach_bio: '',
    scheduling_link: '',
    license_number: '',
    specialities_description: '',
    years_of_experience: undefined,
    client_count: '',
    price_per_hour: undefined,
    speciality_ids: [],
    free_session_scheduling_link: '',
    free_block_session_link: '',
    individual_session_link: '',
    coach_level: 1,
    is_instructor: false,
  });

  // File handling
  const coachPhoto = ref<File | null>(null);
  const coachPhotoPreview = ref<string>('');
  const existingPhotoUrl = ref<string>('');
  const photoInput = ref<HTMLInputElement | null>(null);

  // Form state
  const loading = ref(false);
  const loadingCoach = ref(false);
  const errors = ref<Record<string, string>>({});

  // Success modal
  const showSuccessModal = ref(false);

  // Specialties
  const specialityOptions = ref<SelectOption[]>([]);
  const selectedSpecialities = ref<SelectOption[]>([]);

  // Computed for form validation
  const isFormValid = computed(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailValid =
      formData.value.coach_email && emailRegex.test(formData.value.coach_email);

    return (
      formData.value.coach_name &&
      formData.value.coach_lastname &&
      isEmailValid &&
      formData.value.coach_headline &&
      (coachPhoto.value || existingPhotoUrl.value)
    );
  });

  function hasTransparency(img: HTMLImageElement): boolean {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    canvas.width = img.width;
    canvas.height = img.height;
    ctx.drawImage(img, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    for (let i = 3; i < imageData.length; i += 4) {
      if (imageData[i] < 255) {
        return true;
      }
    }
    return false;
  }

  // Handle file selection
  const handlePhotoChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/avif'];
      if (!allowedTypes.includes(file.type)) {
        errors.value.photo =
          'Invalid file type. Please select JPEG, PNG, or AVIF image.';
        return;
      }

      // Validate file size (optional, e.g., max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        errors.value.photo =
          'File size too large. Please select a file under 5MB.';
        return;
      }

      coachPhoto.value = file;
      errors.value.photo = '';

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          if (hasTransparency(img)) {
            errors.value.photo =
              'Image contains transparent pixels. Please choose another image.';
            coachPhoto.value = null;
            coachPhotoPreview.value = '';
            return;
          }
          coachPhoto.value = file;
          coachPhotoPreview.value = e.target?.result as string;
          errors.value.photo = '';
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  // Trigger file input
  const triggerPhotoUpload = () => {
    photoInput.value?.click();
  };

  // Email validation
  const validateEmail = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (
      formData.value.coach_email &&
      !emailRegex.test(formData.value.coach_email)
    ) {
      errors.value.email = 'Please enter a valid email address.';
    } else {
      errors.value.email = '';
    }
  };

  // Load existing coach data
  const loadCoachData = async () => {
    if (!coachId) {
      router.push('/coaches-list');
      return;
    }

    loadingCoach.value = true;
    errors.value = {};

    try {
      const response = await coachService.getById(coachId);

      // Handle different response formats - API might return data wrapped in a response object
      const coach = (response as { data?: Coach }).data || response;

      // Populate form data by updating individual fields
      formData.value.coach_name = coach.coach_name || '';
      formData.value.coach_lastname = coach.coach_lastname || '';
      formData.value.coach_email = coach.coach_email || '';
      formData.value.coach_headline = coach.coach_headline || '';
      formData.value.coach_bio = coach.coach_bio || '';
      formData.value.scheduling_link = coach.scheduling_link || '';
      formData.value.license_number = coach.license_number || '';
      formData.value.specialities_description =
        coach.specialities_description || '';
      formData.value.years_of_experience = coach.years_of_experience;
      formData.value.client_count = coach.client_count || '';
      formData.value.price_per_hour = coach.price_per_hour;
      formData.value.speciality_ids = coach.speciality_ids || [];
      formData.value.free_session_scheduling_link =
        coach.free_session_scheduling_link || '';
      formData.value.free_block_session_link =
        coach.free_block_scheduling_link || '';
      formData.value.coach_level = coach.coach_level || 1;
      formData.value.is_instructor = coach.is_instructor || false;
      formData.value.individual_session_link =
        coach.individual_session_link || '';

      // Set existing photo
      if (coach.coach_photo) {
        existingPhotoUrl.value = coach.coach_photo;
        coachPhotoPreview.value = coach.coach_photo;
      }

      // Set selected specialities after loading coach data
      setSelectedSpecialities();
    } catch (error) {
      console.error('Error loading coach data:', error);
      errors.value.form = 'Failed to load coach data.';
    } finally {
      loadingCoach.value = false;
    }
  };

  // Submit form
  const submitForm = async () => {
    validateEmail();

    if (!isFormValid.value) {
      errors.value.form =
        'Please fill in all required fields with valid information.';
      return;
    }

    loading.value = true;
    errors.value = {};

    try {
      const files = coachPhoto.value ? [coachPhoto.value] : undefined;
      const response = await coachService.update(
        coachId,
        formData.value,
        files,
      );

      if (response.success || response.data) {
        showSuccessModal.value = true;
      } else {
        errors.value.form = response.message || 'Failed to update coach.';
      }
    } catch (error) {
      console.error('Error updating coach:', error);
      errors.value.form = 'An error occurred while updating the coach.';
    } finally {
      loading.value = false;
    }
  };

  // Fetch specialities
  const fetchSpecialities = async () => {
    try {
      const response = await coachService.getSpecialities();
      specialityOptions.value = transformToSelectOptions(
        response.data,
        'speciality_id',
        'speciality_name',
      );

      // Set selected specialities if coach data already has speciality_ids
      setSelectedSpecialities();
    } catch (error) {
      console.error('Error fetching specialities:', error);
      errors.value.specialities = 'Failed to load specialities.';
    }
  };

  // Set selected specialities based on form data
  const setSelectedSpecialities = () => {
    if (
      formData.value.speciality_ids &&
      formData.value.speciality_ids.length > 0 &&
      specialityOptions.value.length > 0
    ) {
      selectedSpecialities.value = specialityOptions.value.filter((option) =>
        formData.value.speciality_ids?.includes(option.id),
      );
    }
  };

  // Add specialty
  const addSpecialty = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    const specialtyId = target.value;

    if (specialtyId && selectedSpecialities.value.length < 3) {
      const specialty = specialityOptions.value.find(
        (s) => s.id === specialtyId,
      );
      if (
        specialty &&
        !selectedSpecialities.value.some((s) => s.id === specialtyId)
      ) {
        selectedSpecialities.value.push(specialty);
        formData.value.speciality_ids = selectedSpecialities.value.map(
          (s) => s.id,
        );
      }
    }
    target.value = ''; // Reset the select
  };

  // Remove specialty
  const removeSpecialty = (specialty: SelectOption) => {
    selectedSpecialities.value = selectedSpecialities.value.filter(
      (s) => s.id !== specialty.id,
    );
    formData.value.speciality_ids = selectedSpecialities.value.map((s) => s.id);
  };

  // Available specialities (excluding already selected ones)
  const availableSpecialities = computed(() => {
    return specialityOptions.value.filter(
      (option) =>
        !selectedSpecialities.value.some(
          (selected) => selected.id === option.id,
        ),
    );
  });

  // Close modal and navigate to coaches list
  const closeSuccessModal = () => {
    showSuccessModal.value = false;
    router.push('/coaches-list');
  };

  // Initialize data
  onMounted(async () => {
    await fetchSpecialities();
    await loadCoachData();
  });
</script>

<template>
  <div class="coach-edit">
    <TopbarOrganism
      linkText="Back to coaches"
      :showIcon="true"
      icon="angleLeft"
      primaryButtonText="Update Coach"
      route="/coaches-list"
      :onPrimaryClick="submitForm"
      :primaryButtonDisabled="!isFormValid || loading || loadingCoach"
    />

    <div class="coach-edit-content">
      <div class="form-container">
        <h2>Edit Coach</h2>

        <!-- Loading State -->
        <div v-if="loadingCoach" class="loading-state">
          <p>Loading coach data...</p>
        </div>

        <div v-else>
          <!-- Photo Upload -->
          <div class="photo-section">
            <h3>Coach Photo *</h3>
            <div class="photo-upload">
              <input
                ref="photoInput"
                type="file"
                accept="image/jpeg,image/png,image/avif"
                @change="handlePhotoChange"
                style="display: none"
              />
              <div class="photo-preview" v-if="coachPhotoPreview">
                <img :src="coachPhotoPreview" alt="Coach photo preview" />
              </div>
              <ButtonAtom
                class="btn btn-secondary"
                @click="triggerPhotoUpload"
                :disabled="loading"
              >
                {{ coachPhotoPreview ? 'Change Photo' : 'Upload Photo' }}
              </ButtonAtom>
            </div>
            <p v-if="errors.photo" class="error">{{ errors.photo }}</p>
          </div>

          <!-- Basic Info -->
          <div class="form-section">
            <h3>Basic Information</h3>
            <div class="form-row">
              <InputMolecule
                title="First Name*"
                label="Coach's first name"
                id="coach_name"
                placeholder="Enter first name"
                v-model="formData.coach_name"
              />
              <InputMolecule
                title="Last Name*"
                label="Coach's last name"
                id="coach_lastname"
                placeholder="Enter last name"
                v-model="formData.coach_lastname"
              />
            </div>
            <InputMolecule
              title="Email*"
              label="Coach's email address"
              id="coach_email"
              placeholder="Enter email address"
              type="email"
              v-model="formData.coach_email"
              @blur="validateEmail"
            />
            <p v-if="errors.email" class="error">{{ errors.email }}</p>
            <InputMolecule
              title="Headline*"
              label="Professional headline"
              id="coach_headline"
              placeholder="e.g., Certified Life Coach & Mental Health Professional"
              v-model="formData.coach_headline"
            />
            <div class="form-group">
              <h4>Bio</h4>
              <label for="coach_bio">Coach's biography</label>
              <textarea
                id="coach_bio"
                v-model="formData.coach_bio"
                placeholder="Tell us about the coach's background and expertise..."
                rows="4"
                class="textarea"
              ></textarea>
            </div>
          </div>

          <!-- Professional Info -->
          <div class="form-section">
            <h3>Professional Information</h3>
            <!-- <InputMolecule
              title="License Number"
              label="Professional license number"
              id="license_number"
              placeholder="Enter license number"
              v-model="formData.license_number"
            /> -->
            <div class="form-row">
              <InputMolecule
                title="Years of Experience"
                label="Years of professional experience"
                id="years_of_experience"
                placeholder="e.g., 5"
                type="number"
                :modelValue="formData.years_of_experience?.toString() || ''"
                @update:modelValue="
                  formData.years_of_experience = $event
                    ? Number($event)
                    : undefined
                "
              />
              <InputMolecule
                title="Client Count"
                label="Number of clients served"
                id="client_count"
                placeholder="e.g., 100+"
                v-model="formData.client_count"
              />
            </div>
            <div class="form-row">
              <InputMolecule
                title="Price per Hour"
                label="Hourly rate in USD"
                id="price_per_hour"
                placeholder="e.g., 150"
                type="number"
                :modelValue="formData.price_per_hour?.toString() || ''"
                @update:modelValue="
                  formData.price_per_hour = $event ? Number($event) : undefined
                "
              />
              <div class="form-group">
                <h4>Coach Level</h4>
                <label for="coach_level">Select coach experience level</label>
                <select
                  id="coach_level"
                  v-model="formData.coach_level"
                  class="select-input"
                >
                  <option value="1">Level 1</option>
                  <option value="2">Level 2</option>
                  <option value="3">Level 3</option>
                </select>
              </div>
            </div>
            <div class="form-group checkbox-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  v-model="formData.is_instructor"
                  class="checkbox-input"
                />
                <span class="checkbox-text">Is Instructor</span>
              </label>
              <p class="checkbox-description">
                Check if this coach is also an instructor
              </p>
            </div>
          </div>

          <!-- Specialties -->
          <div class="form-section">
            <h3>Specialties</h3>
            <div class="form-group">
              <h4>Specialties Description</h4>
              <label for="specialities_description"
                >Description of specialties</label
              >
              <textarea
                id="specialities_description"
                v-model="formData.specialities_description"
                placeholder="Describe the coach's areas of specialization..."
                rows="3"
                class="textarea"
              ></textarea>
            </div>
            <div class="form-group">
              <h4>Specialties</h4>
              <label for="speciality_ids"
                >Select coach specialties (maximum 3)</label
              >
              <div class="specialty-dropdown">
                <div
                  class="specialty-tags"
                  v-if="selectedSpecialities.length > 0"
                >
                  <div
                    v-for="specialty in selectedSpecialities"
                    :key="specialty.id"
                    class="specialty-tag"
                  >
                    {{ specialty.value }}
                    <span class="remove-tag" @click="removeSpecialty(specialty)"
                      >×</span
                    >
                  </div>
                </div>
                <select
                  v-if="selectedSpecialities.length < 3"
                  @change="addSpecialty($event)"
                  class="specialty-select"
                >
                  <option value="">Choose a specialty...</option>
                  <option
                    v-for="option in availableSpecialities"
                    :key="option.id"
                    :value="option.id"
                  >
                    {{ option.value }}
                  </option>
                </select>
              </div>
              <p v-if="errors.specialities" class="error">
                {{ errors.specialities }}
              </p>
            </div>
          </div>

          <!-- Contact Info -->
          <div class="form-section">
            <h3>Contact Information</h3>
            <InputMolecule
              title="Scheduling Link"
              label="Link for booking appointments"
              id="scheduling_link"
              placeholder="https://calendly.com/coach-name"
              v-model="formData.scheduling_link"
            />
            <InputMolecule
              title="Free Block Scheduling Link"
              label="Link for booking free block session (3 sessions)"
              id="free_block_scheduling_link"
              placeholder="https://calendly.com/coach-name"
              v-model="formData.free_block_session_link"
            />
            <InputMolecule
              title="Individual Session Link"
              label="Link for booking individual session"
              id="individual_session_link"
              placeholder="https://calendly.com/coach-name"
              v-model="formData.individual_session_link"
            />
            <InputMolecule
              title="Free Session Scheduling Link"
              label="Link for booking free sessions"
              id="free_session_scheduling_link"
              placeholder="https://calendly.com/coach-name"
              v-model="formData.free_session_scheduling_link"
            />
          </div>

          <!-- Error Display -->
          <div v-if="errors.form" class="error-section">
            <p class="error">{{ errors.form }}</p>
          </div>

          <!-- Submit Button -->
          <div class="submit-section">
            <ButtonAtom
              class="btn btn-primary"
              @click="submitForm"
              :disabled="!isFormValid || loading"
            >
              {{ loading ? 'Updating...' : 'Update Coach' }}
            </ButtonAtom>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Modal -->
    <div
      v-if="showSuccessModal"
      class="modal-overlay"
      @click="closeSuccessModal"
    >
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Coach Updated Successfully!</h3>
          <button class="close-btn" @click="closeSuccessModal">&times;</button>
        </div>
        <div class="modal-body">
          <p class="success-message">
            The coach profile has been updated successfully.
          </p>
        </div>
        <div class="modal-footer">
          <ButtonAtom
            class="btn btn-primary"
            @click="closeSuccessModal"
            :disabled="false"
          >
            Continue to Coaches List
          </ButtonAtom>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .coach-edit {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .coach-edit-content {
    flex: 1;
    padding: 24px;
    overflow-y: auto;
    background-color: #fff;
  }

  .form-container {
    max-width: 800px;
    margin: 0 auto;
  }

  .form-container h2 {
    margin-bottom: 32px;
    color: #2c3e50;
    font-size: 28px;
    font-weight: 600;
  }

  .loading-state {
    padding: 48px;
    text-align: center;
    border: 2px dashed #e1e5e9;
    border-radius: 12px;
    background-color: #fafafa;
  }

  .loading-state p {
    color: #6b7280;
    font-size: 16px;
    margin: 0;
  }

  .form-section {
    margin-bottom: 32px;
    padding: 24px;
    border: 1px solid #e1e5e9;
    border-radius: 12px;
    background-color: #fafafa;
  }

  .form-section h3 {
    margin-bottom: 20px;
    color: #2c3e50;
    font-size: 20px;
    font-weight: 600;
  }

  .photo-section {
    margin-bottom: 32px;
    padding: 24px;
    border: 1px solid #e1e5e9;
    border-radius: 12px;
    background-color: #fafafa;
  }

  .photo-section h3 {
    margin-bottom: 16px;
    color: #2c3e50;
    font-size: 20px;
    font-weight: 600;
  }

  .photo-upload {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  .photo-preview {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    overflow: hidden;
    border: 3px solid #e1e5e9;
  }

  .photo-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .form-row {
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }

  .form-group {
    margin-bottom: 20px;
  }

  .form-group h4 {
    margin-bottom: 8px;
    color: #2c3e50;
    font-size: 16px;
    font-weight: 600;
  }

  .form-group label {
    display: block;
    margin-bottom: 8px;
    color: #6b7280;
    font-size: 14px;
  }

  .textarea {
    width: 100%;
    padding: 12px 16px;
    border: none;
    border-radius: 8px;
    background-color: var(--color-gray-30);
    resize: vertical;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.5;
  }

  .textarea:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(66, 185, 131, 0.2);
  }

  .error {
    color: #dc2626;
    font-size: 14px;
    margin-top: 4px;
  }

  .error-section {
    margin: 20px 0;
    padding: 16px;
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
  }

  .submit-section {
    margin-top: 32px;
    text-align: center;
  }

  .specialty-dropdown {
    margin-bottom: 16px;
  }

  .specialty-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }

  .specialty-tag {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background-color: #42b983;
    color: white;
    border-radius: 20px;
    font-size: 14px;
  }

  .remove-tag {
    cursor: pointer;
    font-weight: bold;
    width: 24px;
    height: 24px;
    font-size: 16px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.2);
    transition: background-color 0.2s;
  }

  .remove-tag:hover {
    background-color: rgba(255, 255, 255, 0.3);
  }

  .specialty-select {
    width: 100%;
    padding: 12px 16px;
    border: none;
    border-radius: 8px;
    background-color: var(--color-gray-30);
    font-family: inherit;
    font-size: 14px;
    cursor: pointer;
  }

  .specialty-select:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(66, 185, 131, 0.2);
  }

  .select-input {
    width: 100%;
    padding: 12px 16px;
    border: none;
    border-radius: 8px;
    background-color: var(--color-gray-30);
    font-family: inherit;
    font-size: 14px;
    cursor: pointer;
  }

  .select-input:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(66, 185, 131, 0.2);
  }

  .checkbox-group {
    margin-bottom: 0;
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    margin-bottom: 4px;
  }

  .checkbox-input {
    width: 18px;
    height: 18px;
    accent-color: #42b983;
    cursor: pointer;
  }

  .checkbox-text {
    color: #2c3e50;
    font-size: 16px;
    font-weight: 600;
  }

  .checkbox-description {
    color: #6b7280;
    font-size: 14px;
    margin: 0;
    margin-left: 30px;
  }

  /* Modal Styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }

  .modal-content {
    background: white;
    border-radius: 12px;
    width: 90%;
    max-width: 600px;
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 24px 0 24px;
    border-bottom: 1px solid #e1e5e9;
  }

  .modal-header h3 {
    margin: 0;
    color: #2c3e50;
    font-size: 24px;
    font-weight: 600;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 28px;
    color: #6b7280;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.2s;
  }

  .close-btn:hover {
    background-color: #f3f4f6;
  }

  .modal-body {
    padding: 24px;
  }

  .success-message {
    color: #374151;
    font-size: 16px;
    line-height: 1.6;
    margin-bottom: 24px;
  }

  .modal-footer {
    padding: 0 24px 24px 24px;
    display: flex;
    justify-content: flex-end;
    border-top: 1px solid #e1e5e9;
    padding-top: 16px;
  }

  @media (max-width: 768px) {
    .form-row {
      grid-template-columns: 1fr;
    }

    .coach-edit-content {
      padding: 16px;
    }
  }
</style>
